import { getAuthToken } from '../services/auth/tokenStorage';
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { createLogger } from '../utils/logger';
import { runCredentialMigrationIfNeeded } from '../services/encryption/credentialMigration';
import {
  fetchAndOpenPrimaryMasterKey,
  hasCachedPrimaryMasterKey,
  TrustedPartyAccessDeniedError,
  TrustedPartyAccessNotReadyError,
} from '../services/encryption/TrustedPartyAccess';

const logger = createLogger('UserSelection');

// The dashboard a person last chose, per account. A successor or keyholder
// with no vault of their own opens on the primary's dashboard until they
// choose otherwise; everyone else opens on their own.
const selectionKey = (userId: string) => `@selected_dashboard_user:${userId}`;

async function hasOwnVault(token: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/subscription/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return true;
    const status = await response.json();
    // Only an explicit "never started their own trial" counts as no vault;
    // anything unclear keeps the person on their own dashboard.
    return status?.hasStartedOwnTrial !== false;
  } catch {
    return true;
  }
}

export interface PrimaryUser {
  id: string;
  name: string;
  email: string;
  role: 'successor' | 'keyholder';
  isDeathVerified: boolean; // True when death certificate is approved and secret code validated
}

interface UserSelectionContextType {
  currentUserId: string;
  selectedUserId: string;
  isViewingOthersData: boolean;
  availablePrimaryUsers: PrimaryUser[];
  setSelectedUserId: (userId: string) => void;
  loading: boolean;
  isInitialized: boolean; // True once currentUserId has been set
  refreshPrimaryUsers: () => Promise<void>;
  isDeathVerifiedForSelectedUser: boolean; // True when viewing a user whose death has been verified
  selectedUserRole: 'successor' | 'keyholder' | null; // Role for the currently selected primary user
}

const UserSelectionContext = createContext<UserSelectionContextType | undefined>(undefined);

export const UserSelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [selectedUserId, setSelectedUserIdState] = useState<string>('');
  const [availablePrimaryUsers, setAvailablePrimaryUsers] = useState<PrimaryUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Check for token changes periodically and on mount
  useEffect(() => {
    const checkToken = async () => {
      const token = await getAuthToken();
      if (token !== authToken) {
        logger.debug('🔐 [UserSelectionContext] Token changed, updating auth state');
        setAuthToken(token);
      }
    };

    // Check immediately
    checkToken();

    // Set up interval to check for token changes (e.g., after login/logout)
    const interval = setInterval(checkToken, 1000);

    return () => clearInterval(interval);
  }, [authToken]);

  // Fetch current user ID when token changes
  useEffect(() => {
    const fetchCurrentUserId = async () => {
      logger.debug('🆔 [UserSelectionContext] fetchCurrentUserId - authToken exists:', !!authToken);

      if (!authToken) {
        // User logged out - reset all state
        logger.debug('🆔 [UserSelectionContext] No token, resetting state');
        setCurrentUserId('');
        setSelectedUserIdState('');
        setAvailablePrimaryUsers([]);
        setIsInitialized(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        logger.debug('🆔 [UserSelectionContext] Fetching from:', `${API_URL}/users/profile`);
        const response = await fetch(`${API_URL}/users/profile`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        logger.debug('🆔 [UserSelectionContext] Profile response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          logger.debug('🆔 [UserSelectionContext] Profile result:', JSON.stringify(result, null, 2));
          const userId = result.user?.id || result.id;
          logger.debug('🆔 [UserSelectionContext] Setting currentUserId:', userId);

          // Only update if user changed
          if (userId !== currentUserId) {
            logger.debug('🆔 [UserSelectionContext] User changed from', currentUserId, 'to', userId);
            setCurrentUserId(userId);
            setSelectedUserIdState(userId); // Reset to own data on user change
            userChoseRef.current = false;
            setAvailablePrimaryUsers([]); // Clear old primary users
          }
          setIsInitialized(true);
          // K5: an account whose saved logins are still plaintext rewrites
          // them now if the key is on this device (no-op once migrated).
          void runCredentialMigrationIfNeeded();
        } else {
          const errorText = await response.text();
          logger.error('🆔 [UserSelectionContext] Profile fetch failed:', response.status, errorText);
        }
      } catch (error: any) {
        // Suppress network errors - these are expected when backend is unreachable
        if (error.message?.includes('Network request failed') || error.message?.includes('Network Error')) {
          logger.debug('🆔 [UserSelectionContext] Network unavailable, skipping user ID fetch');
        } else {
          logger.error('Error fetching current user ID:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUserId();
  }, [authToken]);

  // Fetch available primary users who granted access
  const refreshPrimaryUsers = async () => {
    logger.debug('🔄 [UserSelectionContext] refreshPrimaryUsers called');
    try {
      const token = await getAuthToken();
      logger.debug('🔑 [UserSelectionContext] Token exists:', !!token);
      if (!token) {
        logger.debug('❌ [UserSelectionContext] No token, skipping fetch');
        return;
      }

      logger.debug('📡 [UserSelectionContext] Fetching from:', `${API_URL}/api/relationships/primary-users`);
      const response = await fetch(`${API_URL}/api/relationships/primary-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      logger.debug('📥 [UserSelectionContext] Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        logger.debug('✅ [UserSelectionContext] Primary users result:', JSON.stringify(result, null, 2));
        const primaryUsers = result.primaryUsers || [];
        setAvailablePrimaryUsers(primaryUsers);
        await applyDefaultSelection(primaryUsers, token);
      } else {
        const errorText = await response.text();
        logger.error('❌ [UserSelectionContext] API error:', response.status, errorText);
      }
    } catch (error: any) {
      // Suppress network errors - these are expected when backend is unreachable
      if (error.message?.includes('Network request failed') || error.message?.includes('Network Error')) {
        logger.debug('🔄 [UserSelectionContext] Network unavailable, skipping primary users fetch');
      } else {
        logger.error('❌ [UserSelectionContext] Error fetching primary users:', error);
      }
      setAvailablePrimaryUsers([]);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      refreshPrimaryUsers();
    }
  }, [currentUserId]);

  // Medium-term #2 — when the user switches to view another primary's
  // data (successor post-death OR keyholder with an active grant), pre-
  // warm the TrustedPartyAccess session cache with that primary's
  // master key. Downstream decryption paths can then synchronously
  // read the unwrapped key from the cache. Best-effort: a backend 403
  // (no gate passed) or 404 (no wrap yet) is logged and ignored so
  // legacy flows continue to work.
  const prewarmPrimaryKey = (userId: string) => {
    if (
      userId &&
      userId !== currentUserId &&
      !hasCachedPrimaryMasterKey(userId)
    ) {
      (async () => {
        try {
          await fetchAndOpenPrimaryMasterKey(userId);
          logger.debug(
            '🔐 [UserSelectionContext] Pre-warmed trusted-party master key for',
            userId,
          );
        } catch (err) {
          if (err instanceof TrustedPartyAccessNotReadyError) {
            logger.debug(
              '🔐 [UserSelectionContext] Own vault not unlocked; trusted-party cache will prime after unlock',
            );
          } else if (err instanceof TrustedPartyAccessDeniedError) {
            logger.debug(
              '🔐 [UserSelectionContext] No trusted-party wrap yet for',
              userId,
              '— falling back to legacy access',
            );
          } else {
            logger.warn('Trusted-party prewarm failed:', err);
          }
        }
      })();
    }
  };

  // True once the person has picked a dashboard this session; the default
  // below never overrides a choice.
  const userChoseRef = useRef(false);

  const setSelectedUserId = (userId: string) => {
    logger.debug('🔄 [UserSelectionContext] setSelectedUserId called with:', userId, '(current:', currentUserId, ', previous selected:', selectedUserId, ')');
    setSelectedUserIdState(userId);
    userChoseRef.current = true;
    if (currentUserId) {
      AsyncStorage.setItem(selectionKey(currentUserId), userId).catch(() => {});
    }
    prewarmPrimaryKey(userId);
  };

  // Which dashboard to open when nobody has chosen yet: the one remembered
  // for this account, else the primary's for an account with no vault of
  // its own, else the person's own.
  const applyDefaultSelection = async (primaryUsers: PrimaryUser[], token: string) => {
    if (userChoseRef.current || !currentUserId) return;
    try {
      const remembered = await AsyncStorage.getItem(selectionKey(currentUserId));
      if (remembered) {
        if (remembered === currentUserId) return;
        if (primaryUsers.some((p) => p.id === remembered)) {
          logger.debug('📌 [UserSelectionContext] Restoring remembered dashboard:', remembered);
          setSelectedUserIdState(remembered);
          prewarmPrimaryKey(remembered);
          return;
        }
      }
      if (primaryUsers.length > 0 && !(await hasOwnVault(token))) {
        logger.debug('📌 [UserSelectionContext] No vault of their own — opening on the primary:', primaryUsers[0].id);
        setSelectedUserIdState(primaryUsers[0].id);
        prewarmPrimaryKey(primaryUsers[0].id);
      }
    } catch (error) {
      logger.debug('Default dashboard selection skipped:', error);
    }
  };

  const isViewingOthersData = selectedUserId !== currentUserId && selectedUserId !== '';

  // Debug logging for viewing state
  logger.debug('👁️ [UserSelectionContext] isViewingOthersData:', isViewingOthersData, '(selectedUserId:', selectedUserId, ', currentUserId:', currentUserId, ')');

  // Get the selected primary user's details (if viewing another user's data)
  const selectedPrimaryUser = availablePrimaryUsers.find(u => u.id === selectedUserId);

  // Check if death has been verified for the selected user
  const isDeathVerifiedForSelectedUser = isViewingOthersData && selectedPrimaryUser?.isDeathVerified === true;

  // Get the role for the selected user (successor or keyholder)
  const selectedUserRole = isViewingOthersData ? (selectedPrimaryUser?.role || null) : null;

  return (
    <UserSelectionContext.Provider
      value={{
        currentUserId,
        selectedUserId,
        isViewingOthersData,
        availablePrimaryUsers,
        setSelectedUserId,
        loading,
        isInitialized,
        refreshPrimaryUsers,
        isDeathVerifiedForSelectedUser,
        selectedUserRole
      }}
    >
      {children}
    </UserSelectionContext.Provider>
  );
};

export const useUserSelection = (): UserSelectionContextType => {
  const context = useContext(UserSelectionContext);
  if (!context) {
    throw new Error('useUserSelection must be used within a UserSelectionProvider');
  }
  return context;
};
